'use client';
import { PROSE_NO_MARGINS } from '../../constants/rich-text-editor.js';
import { cn } from '../../lib/utils.js';
/**
 * Processes HTML content for inline display by converting lists to comma-separated text.
 *
 * @param html - The HTML content to process.
 * @returns The processed HTML suitable for inline display.
 */
function processInlineContent(html) {
    // Create a temporary div to parse the HTML
    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');
    // Process ordered lists - convert to "1. item 2. item 3. item"
    var orderedLists = doc.querySelectorAll('ol');
    orderedLists.forEach(function (ol) {
        var items = Array.from(ol.querySelectorAll('li'));
        var text = items.map(function (li, index) { return "".concat(index + 1, ". ").concat(li.textContent); }).join(' ');
        var span = doc.createElement('span');
        // Add space after to separate from adjacent elements
        span.textContent = "".concat(text, " ");
        ol.replaceWith(span);
    });
    // Process bullet lists - convert to "• item • item • item"
    var bulletLists = doc.querySelectorAll('ul');
    bulletLists.forEach(function (ul) {
        var items = Array.from(ul.querySelectorAll('li'));
        var text = items.map(function (li) { return "\u2022 ".concat(li.textContent); }).join(' ');
        var span = doc.createElement('span');
        // Add space after to separate from adjacent elements
        span.textContent = "".concat(text, " ");
        ul.replaceWith(span);
    });
    // Add spacing to other block elements by wrapping their content
    var blockElements = doc.querySelectorAll('h1, h2, h3, h4, h5, h6, p, blockquote');
    blockElements.forEach(function (element) {
        var textContent = element.textContent || '';
        if (textContent.trim()) {
            // Preserve inner HTML (for links, bold, etc.) but add spacing
            var span = doc.createElement('span');
            span.innerHTML = "".concat(element.innerHTML.trim(), " ");
            element.replaceWith(span);
        }
    });
    return doc.body.innerHTML;
}
/**
 * Displays rich text HTML content with consistent styling.
 *
 * This component renders HTML content from the RichTextEditor with the same
 * prose styling and link formatting to ensure visual consistency.
 *
 * @example
 * ```tsx
 * // Block display (default)
 * <RichTextDisplay content={savedHtmlContent} />
 *
 * // Inline display (no line breaks, lists converted to comma-separated)
 * <RichTextDisplay content={savedHtmlContent} inline />
 * ```
 */
function RichTextDisplay(_a) {
    var content = _a.content, className = _a.className, _b = _a.inline, inline = _b === void 0 ? false : _b, noContentFallback = _a.noContentFallback;
    // Return null for empty content
    if (!content || content === '<p></p>') {
        if (noContentFallback) {
            return <div className={className}>{noContentFallback}</div>;
        }
        return null;
    }
    // Process content for inline display if needed
    var displayContent = inline ? processInlineContent(content) : content;
    return (<div className={cn(
        // Uses @tailwindcss/typography for prose styling to match the editor
        'prose prose-sm dark:prose-invert max-w-none wrap-break-word', 
        // Make prose inherit parent text color instead of applying its own
        '[&]:text-[inherit] [&_*]:text-[inherit]', 
        // Link styling to match the editor's Link extension configuration
        'prose-a:text-primary prose-a:underline prose-a:decoration-primary/30', 'prose-a:underline-offset-4', 
        // Remove margins from block elements
        !inline && PROSE_NO_MARGINS, 
        // Give empty paragraphs minimum height so they take up space like line breaks
        !inline && 'prose-p:empty:min-h-[1.5em] prose-p:empty:block', 
        // Inline mode: remove block spacing and make content flow inline
        inline && 'inline [&>*]:m-0 [&>*]:inline [&>*]:p-0', className)} dangerouslySetInnerHTML={{ __html: displayContent }}/>);
}
export { RichTextDisplay };
